import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PageHero, Reveal, SectionHead } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { WaveMark } from "@/components/Illustrations";
import { WA_VALORACION } from "@/lib/site";

const SobreMi = () => (
  <div data-testid="sobre-mi-page">
    <PageHero
      eyebrow="Sobre mí"
      lines={["Detrás de la técnica,", "hay una historia."]}
      dark
    />

    <section className="bg-cream px-6 sm:px-12 lg:px-20 py-16 lg:py-28" data-testid="sobre-mi-historia">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative inline-block"
          >
            <div className="absolute -inset-4 border border-gold/50 pointer-events-none" />
            <img
              src="/images/karina-firma.jpg"
              alt="Retrato profesional de Karina Méndez"
              data-testid="sobre-mi-portrait"
              className="w-full max-w-sm object-contain bg-ink"
            />
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mt-6">
              Karina Méndez — La Guaira → La Gomera
            </p>
          </motion.div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7 space-y-7 text-base leading-relaxed text-ink/80">
          {[
            "Nací en La Guaira, Venezuela, y desde muy joven encontré en la belleza una forma de construir. Empecé aprendiendo entre peluquerías familiares, con la manicura y la depilación con hilo como mis primeros oficios.",
            "Emprendí un nuevo camino hacia España en 2017, y llegué a las Islas Canarias, donde un espacio diferenciador y un clima distinto dieron inicio a un propósito: soy la creadora de la Técnica NAGI, el resultado de formarme junto a grandes referentes de la industria e investigar a fondo pigmentos, agujas y comportamiento en piel, frente a un sol y un clima que exigen resultados realmente duraderos en piel sensible. Así nació un método propio: resultados naturales, sin efecto tatuaje.",
            "Muchas mujeres han confiado en mí, en mi trabajo y en mis resultados — y eso es lo que un buen trabajo y profesionalismo constante construyen con el tiempo.",
            "Hoy mi propósito es simple: seguir creciendo, y ayudar a otras a construir. A quien confía en mí como clienta, y a quien quiere formarse conmigo como alumna, les ofrezco lo mismo — una técnica probada, un criterio claro, y la misma disciplina que me trajo hasta aquí.",
          ].map((p, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <p className={i === 1 ? "first-letter:font-serif first-letter:text-5xl first-letter:text-gold first-letter:mr-2 first-letter:float-left first-letter:leading-[0.8]" : ""}>
                {p}
              </p>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <p className="font-serif italic text-3xl text-gold pt-4" data-testid="sobre-mi-lema">Crear, creer, crecer.</p>
          </Reveal>
        </div>
      </div>
    </section>

    <Marquee dark={false} />

    <section className="bg-ink text-cream px-6 sm:px-12 lg:px-20 py-20 lg:py-28" data-testid="sobre-mi-valores">
      <SectionHead dark eyebrow="Lo que me define" title={<>Tres pilares, <span className="italic text-gold">un criterio</span></>} />
      <div className="grid sm:grid-cols-3 gap-px bg-cream/10 border border-cream/10 mt-14">
        {[
          ["01", "Técnica probada", "Cada procedimiento parte de investigación real: pigmentos, agujas y comportamiento en piel bajo el sol canario."],
          ["02", "Criterio claro", "No diseño para todas igual. Cada rostro, cada piel y cada historia piden una decisión distinta."],
          ["03", "Disciplina constante", "La misma que me trajo desde La Guaira hasta aquí — y la que pongo en cada cita y cada alumna."],
        ].map(([n, t, d], i) => (
          <Reveal key={n} delay={i * 0.12} className="bg-ink">
            <div className="p-8 lg:p-12" data-testid={`valor-${n}`}>
              <span className="font-mono text-xs text-gold tracking-[0.25em]">{n}</span>
              <h3 className="font-serif text-2xl mt-5 mb-4">{t}</h3>
              <p className="text-sm text-cream/60 leading-relaxed">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-14">
        <a
          href={WA_VALORACION}
          target="_blank"
          rel="noreferrer"
          data-testid="sobre-mi-cta"
          className="inline-flex items-center gap-3 rounded-full bg-gold text-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:bg-cream transition-colors duration-300"
        >
          Reserva tu valoración <ArrowRight size={15} />
        </a>
      </Reveal>
      <WaveMark className="w-40 text-gold/20 mt-16" />
    </section>
  </div>
);

export default SobreMi;
